import {
  BarChart3,
  ChevronDown,
  ClipboardList,
  FileText,
  LogOut,
  Menu,
  Settings,
  Shield,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/auth_store';
import { Logo } from './brand/Logo';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/denials', label: 'Denials', icon: <ClipboardList className="h-4 w-4" /> },
  { to: '/appeals', label: 'Appeals', icon: <FileText className="h-4 w-4" /> },
  { to: '/dashboard', label: 'Dashboard', icon: <BarChart3 className="h-4 w-4" /> },
  { to: '/settings', label: 'Settings', icon: <Settings className="h-4 w-4" />, roles: ['admin'] },
  { to: '/audit', label: 'Audit Log', icon: <Shield className="h-4 w-4" />, roles: ['admin'] },
];

export function Layout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visibleNav = NAV_ITEMS.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role)),
  );

  async function handleLogout() {
    setUserMenuOpen(false);
    setSidebarOpen(false);
    await logout();
    navigate('/login', { replace: true });
    toast.info('You have been signed out.');
  }

  function handleNavClick() {
    setSidebarOpen(false);
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-56 flex-shrink-0 flex-col bg-gray-900 transition-transform duration-200 lg:relative lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 items-center justify-between border-b border-gray-800 px-4">
          <Logo variant="onDark" />
          <button
            type="button"
            className="ml-2 text-gray-400 hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Main navigation">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-700 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User menu */}
        <div className="relative border-t border-gray-800 p-3">
          <button
            type="button"
            onClick={() => setUserMenuOpen((o) => !o)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
          >
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 truncate">{user?.full_name ?? user?.email}</span>
            <ChevronDown className="h-3 w-3 flex-shrink-0" />
          </button>

          {userMenuOpen && (
            <div className="absolute bottom-full left-2 right-2 mb-1 rounded-md border border-gray-700 bg-gray-800 py-1 shadow-lg">
              <div className="border-b border-gray-700 px-3 py-2">
                <p className="truncate text-xs font-medium text-white">{user?.full_name}</p>
                <p className="truncate text-[11px] text-gray-400">{user?.email}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:bg-gray-700 hover:text-white"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex h-14 flex-shrink-0 items-center border-b border-gray-200 bg-white px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="mr-3 text-gray-500 hover:text-gray-900"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo />
        </div>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
